// Browser-local favorites + recently-viewed for the color picker.
// Single source of truth — color picker, studio, future dashboard all read from here.

import type { SelectedColor } from "@/components/colors/ColorPicker";

const FAV_KEY = "mp_studio_favs";
const RECENT_KEY = "mp_studio_recents";
const RECENT_MAX = 24;

function read<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write<T>(key: string, value: T) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage full or unavailable — silently skip.
  }
}

export function getFavorites(): SelectedColor[] {
  return read<SelectedColor[]>(FAV_KEY, []);
}

export function isFavorite(id: string): boolean {
  return getFavorites().some((c) => c.id === id);
}

export function toggleFavorite(color: SelectedColor): boolean {
  const list = getFavorites();
  const idx = list.findIndex((c) => c.id === color.id);
  if (idx >= 0) {
    list.splice(idx, 1);
    write(FAV_KEY, list);
    return false;
  }
  list.unshift(color);
  write(FAV_KEY, list.slice(0, 100));
  return true;
}

export function getRecentColors(): SelectedColor[] {
  return read<SelectedColor[]>(RECENT_KEY, []);
}

export function pushRecentColor(color: SelectedColor) {
  const list = getRecentColors().filter((c) => c.id !== color.id);
  list.unshift(color);
  write(RECENT_KEY, list.slice(0, RECENT_MAX));
}